import { useState } from 'react'
import { Box, Dialog, IconButton } from '@mui/material'
import { PlayCircleOutlined, Close } from '@mui/icons-material'
import { MediaAttachment } from '@/types'

interface MediaDisplayProps {
  attachments: MediaAttachment[]
}

export default function MediaDisplay({ attachments }: MediaDisplayProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image')

  if (!attachments || attachments.length === 0) return null

  const handleOpen = (url: string, contentType: string) => {
    setLightboxUrl(url)
    setLightboxType(contentType.startsWith('video/') ? 'video' : 'image')
  }

  const handleClose = () => setLightboxUrl(null)

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {attachments.map(att => (
          <Box
            key={att.id}
            onClick={() => handleOpen(att.url, att.contentType)}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { opacity: 0.85 },
            }}
          >
            {att.contentType.startsWith('image/') ? (
              <Box
                component="img"
                src={att.url}
                alt={att.fileName}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'action.hover',
                }}
              >
                <PlayCircleOutlined sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Dialog open={!!lightboxUrl} onClose={handleClose} maxWidth="lg">
        <Box sx={{ position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
          >
            <Close />
          </IconButton>
          {lightboxUrl && lightboxType === 'image' && (
            <Box
              component="img"
              src={lightboxUrl}
              alt="Media"
              sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
            />
          )}
          {lightboxUrl && lightboxType === 'video' && (
            <Box
              component="video"
              src={lightboxUrl}
              controls
              autoPlay
              sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
            />
          )}
        </Box>
      </Dialog>
    </>
  )
}
